import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  MultiSelectCombobox,
  type ComboboxOption,
} from "../../containers/InfoHub/Components/Form/Components/MultiSelectCombobox";
import { type AdminAreaChainItem, formatAggregatorLabel } from "../../utils/adminArea";
import { FILTER_ALL_VALUE, isFilterSet } from "../../utils/filters";

export interface AggregatorAreaOption {
  id: number;
  name: string;
  admin_area?: AdminAreaChainItem[] | null;
}

/** The aggregator ids of a filter value: "" and "all" (every aggregator) pick none. */
export const splitAggregatorIds = (value: string): string[] =>
  isFilterSet(value) ? value.split(",").filter(Boolean) : [];

// A string value, so the options also serve as the filter bar's option labels.
export interface AggregatorAreaComboboxOption extends ComboboxOption {
  value: string;
}

export const buildAggregatorAreaOptions = (
  aggregators: AggregatorAreaOption[]
): AggregatorAreaComboboxOption[] =>
  aggregators
    .map(aggregator => ({ value: String(aggregator.id), label: formatAggregatorLabel(aggregator) }))
    .sort((first, second) => first.label.localeCompare(second.label));

interface AggregatorAreaFilterProps {
  id: string;
  options: AggregatorAreaComboboxOption[];
  // Comma separated aggregator ids, "" for none, "all" when every aggregator is picked: the list
  // is then the unfiltered one, so the filter is left out of the query and the URL stays short.
  value: string;
  onChange: (value: string) => void;
}

/**
 * "Aggregator and Admin Area" filter: aggregators labelled with their admin area chain, so a
 * search on an area followed by "Select all" picks every aggregator in it.
 */
export const AggregatorAreaFilter = ({
  id,
  options,
  value,
  onChange,
}: AggregatorAreaFilterProps) => {
  const { t } = useTranslation();

  const selected = useMemo(() => {
    if (value === FILTER_ALL_VALUE) {
      return options;
    }
    const ids = new Set(splitAggregatorIds(value));
    return options.filter(option => ids.has(option.value));
  }, [options, value]);

  const handleChange = (next: AggregatorAreaComboboxOption[]) => {
    if (!next.length) {
      onChange("");
    } else if (next.length === options.length) {
      onChange(FILTER_ALL_VALUE);
    } else {
      onChange(next.map(option => option.value).join(","));
    }
  };

  return (
    <MultiSelectCombobox<AggregatorAreaComboboxOption>
      id={id}
      options={options}
      value={selected}
      placeholder={t("filters.all")}
      searchPlaceholder={t("filters.aggregatorAreaSearch")}
      clearLabel={t("filters.clearAggregatorArea")}
      onClear={() => onChange("")}
      showSelectAll
      summarizeSelectionAfter={1}
      onChange={handleChange}
    />
  );
};
